/**
 * Architecture Agent Service
 *
 * This service provides frontend integration with the Krysalis Business Architecture Agent,
 * enabling code validation, architectural guidance, and best practice recommendations.
 */

import { BaseService } from './BaseService';

// Types for Architecture Agent
export interface ValidationResult {
  level: 'error' | 'warning' | 'info';
  pattern_type: string;
  message: string;
  code_snippet?: string;
  recommendation?: string;
  line_number?: number;
}

export interface CodeValidationRequest {
  code: string;
  component_type: 'service' | 'endpoint' | 'model' | 'frontend_service' | 'frontend_component';
  component_name: string;
}

export interface CodeQualityAnalysis {
  async_usage: number;
  error_handling: number;
  type_hints: number;
  documentation: number;
  validation: number;
  recommendations: string[];
}

export interface CodeQualityAnalysisRequest {
  code: string;
  file_type: string;
}

export interface RelationshipValidationRequest {
  relationships: Array<{
    source_artifact_id: string;
    target_artifact_id: string;
    relationship_type: 'PROCESSITEM' | 'RESOURCE' | 'MATERIAL' | 'CREATION';
    direction: 'INCOMING' | 'OUTGOING';
    metadata?: Record<string, unknown>;
  }>;
}

export interface BestPracticesRequest {
  area:
    | 'session_management'
    | 'api_design'
    | 'model_design'
    | 'prime_model'
    | 'artifact_instance'
    | 'relationship_management';
}

export interface BestPracticesResponse {
  area: string;
  practices: string[];
}

export interface ArchitectureGuidanceRequest {
  scenario:
    | 'new_service'
    | 'new_endpoint'
    | 'new_model'
    | 'prime_artifact_creation'
    | 'artifact_instance_creation';
}

export interface ArchitectureGuidanceResponse {
  scenario: string;
  patterns: string[];
  recommendations: string[];
  examples: string[];
  validation_rules: string[];
}

export interface AvailableOptions {
  available_scenarios?: string[];
  available_areas?: string[];
  component_types?: string[];
  validation_levels?: string[];
  pattern_types?: string[];
}

export class ArchitectureAgentService extends BaseService {
  private static baseUrl = '/architecture-agent';

  /**
   * Validate code against architectural patterns
   */
  static async validateCode(request: CodeValidationRequest): Promise<ValidationResult[]> {
    try {
      return await this.post<ValidationResult[]>(
        `${this.baseUrl}/validate-code`,
        request as unknown as Record<string, unknown>
      );
    } catch (error) {
      console.error('Code validation failed:', error);
      throw error;
    }
  }

  /**
   * Analyze code quality and provide metrics
   */
  static async analyzeCodeQuality(
    request: CodeQualityAnalysisRequest
  ): Promise<CodeQualityAnalysis> {
    try {
      return await this.post<CodeQualityAnalysis>(
        `${this.baseUrl}/analyze-code-quality`,
        request as unknown as Record<string, unknown>
      );
    } catch (error) {
      console.error('Code quality analysis failed:', error);
      throw error;
    }
  }

  /**
   * Validate relationships against the transition-based relationship model
   */
  static async validateRelationships(
    request: RelationshipValidationRequest
  ): Promise<ValidationResult[]> {
    try {
      return await this.post<ValidationResult[]>(
        `${this.baseUrl}/validate-relationships`,
        request as unknown as Record<string, unknown>
      );
    } catch (error) {
      console.error('Relationship validation failed:', error);
      throw error;
    }
  }

  /**
   * Get best practices for a specific area
   */
  static async getBestPractices(request: BestPracticesRequest): Promise<BestPracticesResponse> {
    try {
      return await this.post<BestPracticesResponse>(
        `${this.baseUrl}/best-practices`,
        request as unknown as Record<string, unknown>
      );
    } catch (error) {
      console.error('Failed to get best practices:', error);
      throw error;
    }
  }

  /**
   * Get architectural guidance for a specific scenario
   */
  static async getArchitectureGuidance(
    request: ArchitectureGuidanceRequest
  ): Promise<ArchitectureGuidanceResponse> {
    try {
      return await this.post<ArchitectureGuidanceResponse>(
        `${this.baseUrl}/architecture-guidance`,
        request as unknown as Record<string, unknown>
      );
    } catch (error) {
      console.error('Failed to get architecture guidance:', error);
      throw error;
    }
  }

  /**
   * Get available scenarios for architecture guidance
   */
  static async getAvailableScenarios(): Promise<string[]> {
    try {
      const response = await this.get<{ available_scenarios: string[] }>(
        `${this.baseUrl}/available-scenarios`
      );
      return response.available_scenarios;
    } catch (error) {
      console.error('Failed to get available scenarios:', error);
      throw error;
    }
  }

  /**
   * Get available areas for best practices
   */
  static async getAvailableAreas(): Promise<string[]> {
    try {
      const response = await this.get<{ available_areas: string[] }>(
        `${this.baseUrl}/available-areas`
      );
      return response.available_areas;
    } catch (error) {
      console.error('Failed to get available areas:', error);
      throw error;
    }
  }

  /**
   * Get supported component types for validation
   */
  static async getComponentTypes(): Promise<string[]> {
    try {
      const response = await this.get<{ component_types: string[] }>(
        `${this.baseUrl}/component-types`
      );
      return response.component_types;
    } catch (error) {
      console.error('Failed to get component types:', error);
      throw error;
    }
  }

  /**
   * Get validation levels
   */
  static async getValidationLevels(): Promise<string[]> {
    try {
      const response = await this.get<{ validation_levels: string[] }>(
        `${this.baseUrl}/validation-levels`
      );
      return response.validation_levels;
    } catch (error) {
      console.error('Failed to get validation levels:', error);
      throw error;
    }
  }

  /**
   * Get pattern types
   */
  static async getPatternTypes(): Promise<string[]> {
    try {
      const response = await this.get<{ pattern_types: string[] }>(`${this.baseUrl}/pattern-types`);
      return response.pattern_types;
    } catch (error) {
      console.error('Failed to get pattern types:', error);
      throw error;
    }
  }

  /**
   * Get all available options for the architecture agent
   */
  static async getAllAvailableOptions(): Promise<AvailableOptions> {
    try {
      const [scenarios, areas, componentTypes, validationLevels, patternTypes] = await Promise.all([
        this.getAvailableScenarios(),
        this.getAvailableAreas(),
        this.getComponentTypes(),
        this.getValidationLevels(),
        this.getPatternTypes(),
      ]);

      return {
        available_scenarios: scenarios,
        available_areas: areas,
        component_types: componentTypes,
        validation_levels: validationLevels,
        pattern_types: patternTypes,
      };
    } catch (error) {
      console.error('Failed to get all available options:', error);
      throw error;
    }
  }

  /**
   * Comprehensive validation of a complete component
   */
  static async validateComponent(
    code: string,
    componentType: CodeValidationRequest['component_type'],
    componentName: string,
    fileType: string
  ): Promise<{
    validationResults: ValidationResult[];
    qualityAnalysis: CodeQualityAnalysis;
  }> {
    try {
      const [validationResults, qualityAnalysis] = await Promise.all([
        this.validateCode({
          code,
          component_type: componentType,
          component_name: componentName,
        }),
        this.analyzeCodeQuality({
          code,
          file_type: fileType,
        }),
      ]);

      return {
        validationResults,
        qualityAnalysis,
      };
    } catch (error) {
      console.error('Component validation failed:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive guidance for a development scenario
   */
  static async getComprehensiveGuidance(
    scenario: ArchitectureGuidanceRequest['scenario']
  ): Promise<{
    guidance: ArchitectureGuidanceResponse;
    bestPractices: BestPracticesResponse[];
  }> {
    try {
      const guidance = await this.getArchitectureGuidance({ scenario });

      // Get best practices for related areas
      const relatedAreas = this.getRelatedAreas(scenario);
      const bestPracticesPromises = relatedAreas.map((area) => this.getBestPractices({ area }));
      const bestPractices = await Promise.all(bestPracticesPromises);

      return {
        guidance,
        bestPractices,
      };
    } catch (error) {
      console.error('Failed to get comprehensive guidance:', error);
      throw error;
    }
  }

  /**
   * Get related areas for a given scenario
   */
  private static getRelatedAreas(scenario: string): BestPracticesRequest['area'][] {
    const areaMap: Record<string, BestPracticesRequest['area'][]> = {
      new_service: ['session_management', 'api_design'],
      new_endpoint: ['api_design', 'session_management'],
      new_model: ['model_design', 'session_management'],
      prime_artifact_creation: ['prime_model', 'relationship_management'],
      artifact_instance_creation: ['artifact_instance', 'relationship_management'],
    };

    return areaMap[scenario] || [];
  }

  /**
   * Format validation results for display
   */
  static formatValidationResults(results: ValidationResult[]): {
    errors: ValidationResult[];
    warnings: ValidationResult[];
    info: ValidationResult[];
  } {
    return {
      errors: results.filter((r) => r.level === 'error'),
      warnings: results.filter((r) => r.level === 'warning'),
      info: results.filter((r) => r.level === 'info'),
    };
  }

  /**
   * Get validation summary
   */
  static getValidationSummary(results: ValidationResult[]): {
    total: number;
    errors: number;
    warnings: number;
    info: number;
    hasIssues: boolean;
  } {
    const errors = results.filter((r) => r.level === 'error').length;
    const warnings = results.filter((r) => r.level === 'warning').length;
    const info = results.filter((r) => r.level === 'info').length;

    return {
      total: results.length,
      errors,
      warnings,
      info,
      hasIssues: errors > 0 || warnings > 0,
    };
  }
}

// Export the class directly since all methods are static
export const architectureAgentService = ArchitectureAgentService;
